/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal, Select, Spin } from 'antd';

import Utils from '../../Assets/Scripts/Utils';
import { fetchComments } from '../../Pages/ScheduleConecta/API/ScheduleSchedulingAPI';
import { api } from '../../Services/axiosService';

const { Option } = Select;

function OccurrencesPopup({ isVisible, onClose, currentOpenTask, setComments, task }) {
  const [form] = Form.useForm();
  const [allOccurrences, setAllOccurrences] = useState([]);
  const [filteredOccurrences, setFilteredOccurrences] = useState([]);
  const [occurrenceTypes, setOccurrenceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formComplete, setFormComplete] = useState(false);

  // Função para buscar as ocorrências do banco de dados, excluindo as inativas
  const fetchOccurrences = async () => {
    setLoading(true);
    try {
      // Use server-side filtering to get only active occurrences
      const response = await api.get(
        `/Occurrence?filters[0].Field=Status&filters[0].Condition=DIFFERENT&filters[0].Value=Inativo`
      );
      // Sort occurrences alphabetically by name
      const sortedOccurrences = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setAllOccurrences(sortedOccurrences);
      // Don't set filtered occurrences here, wait until a type is selected
    } catch (error) {
      message.error('Oops. Algo deu errado ao tentar buscar as Ocorrências!');
      Utils.logError(error);
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar os tipos de ocorrência
  const fetchOccurrenceTypes = async () => {
    setLoading(true);
    try {
      // Use server-side filtering to get only active occurrence types
      const response = await api.get(
        `/OccurrenceType?filters[0].Field=Status&filters[0].Condition=DIFFERENT&filters[0].Value=Inativo`
      );
      // Sort occurrence types alphabetically by name
      const sortedTypes = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setOccurrenceTypes(sortedTypes);
    } catch (error) {
      message.error('Oops. Algo deu errado ao tentar buscar os Tipos de Ocorrência!');
      Utils.logError(error);
    } finally {
      setLoading(false);
    }
  };

  // Função para filtrar ocorrências por tipo
  const filterOccurrencesByType = (typeId) => {
    if (!typeId) {
      setFilteredOccurrences([]); // Clear filtered occurrences if no type selected
      return;
    }

    const filtered = allOccurrences.filter(
      (occurrence) => occurrence.type && occurrence.type.id === typeId
    );
    // Make sure filtered occurrences are also sorted
    const sortedFiltered = filtered.sort((a, b) => a.name.localeCompare(b.name));
    setFilteredOccurrences(sortedFiltered);
  };

  const saveTaskComment = async (occurrenceData, eventSource) => {
    try {
      const userData = JSON.parse(localStorage.getItem('conecta__userData'));
      const title = `Ocorrência ${occurrenceData.occurrenceTypeName}`;

      const data = {
        taskId: currentOpenTask.id,
        userName: userData.name,
        userAvatar: userData.profilePic,
        title,
        description: occurrenceData.observations ?? '',
        sourceId: parseInt(userData?.id, 10),
        sourceType: eventSource,
        activityType: 1,
        occurrenceId: occurrenceData.occurrence?.id || null,
        occurrence: occurrenceData.occurrence?.name || null,
      };

      await api.post('/TaskComment', data).then((res) => res);
      message.success('Ocorrência adicionada!');
    } catch (error) {
      message.error('Oops. Algo deu errado ao tentar adicionar uma Ocorrência!');
      Utils.logError(error);
    }
  };

  useEffect(() => {
    fetchOccurrences();
    fetchOccurrenceTypes();
  }, []);

  // Effect to filter occurrences when selectedType changes
  useEffect(() => {
    filterOccurrencesByType(selectedType);
  }, [selectedType, allOccurrences]);

  // Check if form has both type and occurrence selected
  useEffect(() => {
    const values = form.getFieldsValue();
    const isComplete = values.occurrenceTypeId && values.occurrence;
    setFormComplete(isComplete);
  }, [form, selectedType, filteredOccurrences]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isVisible) {
      form.resetFields();
      setSelectedType(null);
      setFilteredOccurrences([]);
      setFormComplete(false);
    }
  }, [isVisible, form]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();

      // Find the selected occurrence type
      const selectedOccType = occurrenceTypes.find((type) => type.id === values.occurrenceTypeId);

      // Find the selected occurrence
      const occurrence = allOccurrences.find((occ) => occ.name === values.occurrence);

      // Determine the event source based on the occurrence type
      const eventSource = `${selectedOccType?.name}NewOccurrence`;

      const occurrenceData = {
        ...values,
        id: currentOpenTask.id,
        occurrenceTypeId: selectedOccType?.id,
        occurrenceTypeName: selectedOccType?.name,
        occurrence,
      };

      await saveTaskComment(occurrenceData, eventSource);
      form.resetFields();

      const commentsCombined = await fetchComments(task);

      setComments(commentsCombined);

      onClose();
    } catch (error) {
      // Form validation error or API error
      if (error.errorFields) {
        // This is a form validation error, already handled by form
      } else {
        message.error('Erro ao salvar a ocorrência');
        Utils.logError(error);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const isComplete = values.occurrenceTypeId && values.occurrence;
    setFormComplete(isComplete);
  };

  return (
    <Modal
      visible={isVisible}
      title="Registrar Ocorrência"
      onCancel={!saving ? onClose : undefined}
      maskClosable={!saving}
      closable={!saving}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSave}
          disabled={!formComplete || saving}
          loading={saving}
        >
          Salvar
        </Button>,
      ]}
    >
      <Spin spinning={saving} tip="Salvando...">
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          <Form.Item
            name="occurrenceTypeId"
            label="Tipo de Ocorrência"
            rules={[{ required: true, message: 'Por favor, selecione o tipo de ocorrência!' }]}
          >
            <Select
              placeholder="Selecione o tipo"
              loading={loading}
              disabled={saving}
              style={{ borderRadius: '10px' }}
              dropdownStyle={{ borderRadius: '10px' }}
              onChange={(val) => {
                form.resetFields(['occurrence']); // Reset the occurrence selection when type changes
                setSelectedType(val);
              }}
            >
              {occurrenceTypes?.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="occurrence"
            label="Ocorrência"
            rules={[{ required: true, message: 'Por favor, selecione uma ocorrência!' }]}
          >
            <Select
              placeholder="Selecione uma ocorrência"
              loading={loading}
              disabled={!selectedType || saving}
              style={{ borderRadius: '10px' }}
              dropdownStyle={{ borderRadius: '10px' }}
            >
              {filteredOccurrences?.map((item) => (
                <Option key={item.id} value={item.name}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="observations" label="Observações">
            <Input.TextArea
              rows={4}
              placeholder="Adicione observações"
              style={{ borderRadius: '10px' }}
              disabled={
                !form.getFieldValue('occurrenceTypeId') ||
                !form.getFieldValue('occurrence') ||
                saving
              }
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}

export default OccurrencesPopup;
